"use client";

import { useTranslations } from "next-intl";
import { useParams, useSearchParams } from "next/navigation";
import { useState, useTransition } from "react";
import { toast } from "sonner";
import { useOptionalFeatureFlags } from "@/context/feature-flags-context";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { updateUserLanguage } from "@/lib/actions";
import { type Locale } from "@/lib/lang";
import { offeredLocalesFor } from "@/lib/locale-preference";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "./dropdown-menu";

const GlobeIcon = ({ color }: { color: string }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_lang_globe"
      style={{ maskType: "alpha" }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="22"
      height="22"
    >
      <rect width="22" height="22" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_lang_globe)">
      <path
        d="M7.45052 19.4435C6.33524 18.9622 5.36128 18.3053 4.52865 17.4727C3.69601 16.64 3.03906 15.6661 2.55781 14.5508C2.07656 13.4355 1.83594 12.2477 1.83594 10.9872C1.83594 9.72682 2.07656 8.5428 2.55781 7.43516C3.03906 6.32752 3.69601 5.35738 4.52865 4.52474C5.36128 3.6921 6.33524 3.03516 7.45052 2.55391C8.5658 2.07266 9.75365 1.83203 11.0141 1.83203C12.2745 1.83203 13.4585 2.07266 14.5661 2.55391C15.6738 3.03516 16.6439 3.6921 17.4766 4.52474C18.3092 5.35738 18.9661 6.32752 19.4474 7.43516C19.9286 8.5428 20.1693 9.72682 20.1693 10.9872C20.1693 12.2477 19.9286 13.4355 19.4474 14.5508C18.9661 15.6661 18.3092 16.64 17.4766 17.4727C16.6439 18.3053 15.6738 18.9622 14.5661 19.4435C13.4585 19.9247 12.2745 20.1654 11.0141 20.1654C9.75365 20.1654 8.5658 19.9247 7.45052 19.4435ZM11.0026 18.2862C11.3998 17.7362 11.7436 17.1633 12.0339 16.5674C12.3241 15.9716 12.5609 15.3376 12.7443 14.6654H9.26094C9.44427 15.3376 9.68108 15.9716 9.97135 16.5674C10.2616 17.1633 10.6054 17.7362 11.0026 18.2862ZM8.61927 17.9195C8.34427 17.4154 8.10365 16.8921 7.8974 16.3497C7.69115 15.8074 7.51927 15.2459 7.38177 14.6654H4.6776C5.12066 15.4293 5.67448 16.0938 6.33906 16.6591C7.00365 17.2244 7.76372 17.6445 8.61927 17.9195ZM13.3859 17.9195C14.2415 17.6445 15.0016 17.2244 15.6661 16.6591C16.3307 16.0938 16.8845 15.4293 17.3276 14.6654H14.6234C14.4859 15.2459 14.3141 15.8074 14.1078 16.3497C13.9016 16.8921 13.6609 17.4154 13.3859 17.9195ZM3.89844 12.832H7.0151C6.96927 12.5265 6.9349 12.2247 6.91198 11.9268C6.88906 11.6289 6.8776 11.3195 6.8776 10.9987C6.8776 10.6779 6.88906 10.3685 6.91198 10.0706C6.9349 9.77266 6.96927 9.47092 7.0151 9.16536H3.89844C3.82205 9.47092 3.76476 9.77266 3.72656 10.0706C3.68837 10.3685 3.66927 10.6779 3.66927 10.9987C3.66927 11.3195 3.68837 11.6289 3.72656 11.9268C3.76476 12.2247 3.82205 12.5265 3.89844 12.832ZM8.84844 12.832H13.1568C13.2026 12.5265 13.237 12.2247 13.2599 11.9268C13.2828 11.6289 13.2943 11.3195 13.2943 10.9987C13.2943 10.6779 13.2828 10.3685 13.2599 10.0706C13.237 9.77266 13.2026 9.47092 13.1568 9.16536H8.84844C8.8026 9.47092 8.76823 9.77266 8.74531 10.0706C8.7224 10.3685 8.71094 10.6779 8.71094 10.9987C8.71094 11.3195 8.7224 11.6289 8.74531 11.9268C8.76823 12.2247 8.8026 12.5265 8.84844 12.832ZM14.9901 12.832H18.1068C18.1832 12.5265 18.2405 12.2247 18.2786 11.9268C18.3168 11.6289 18.3359 11.3195 18.3359 10.9987C18.3359 10.6779 18.3168 10.3685 18.2786 10.0706C18.2405 9.77266 18.1832 9.47092 18.1068 9.16536H14.9901C15.0359 9.47092 15.0703 9.77266 15.0932 10.0706C15.1161 10.3685 15.1276 10.6779 15.1276 10.9987C15.1276 11.3195 15.1161 11.6289 15.0932 11.9268C15.0703 12.2247 15.0359 12.5265 14.9901 12.832ZM14.6234 7.33203H17.3276C16.8845 6.56814 16.3307 5.90356 15.6661 5.33828C15.0016 4.773 14.2415 4.35286 13.3859 4.07786C13.6609 4.58203 13.9016 5.1053 14.1078 5.64766C14.3141 6.19002 14.4859 6.75148 14.6234 7.33203ZM9.26094 7.33203H12.7443C12.5609 6.65981 12.3241 6.02578 12.0339 5.42995C11.7436 4.83411 11.3998 4.2612 11.0026 3.7112C10.6054 4.2612 10.2616 4.83411 9.97135 5.42995C9.68108 6.02578 9.44427 6.65981 9.26094 7.33203ZM4.6776 7.33203H7.38177C7.51927 6.75148 7.69115 6.19002 7.8974 5.64766C8.10365 5.1053 8.34427 4.58203 8.61927 4.07786C7.76372 4.35286 7.00365 4.773 6.33906 5.33828C5.67448 5.90356 5.12066 6.56814 4.6776 7.33203Z"
        fill={color}
      />
    </g>
  </svg>
);

const ChevronDownIcon = ({ color }: { color: string }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_lang_chevron_down"
      style={{ maskType: "alpha" }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="22"
      height="22"
    >
      <rect width="22" height="22" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_lang_chevron_down)">
      <path
        d="M11 14.1154L5.5 8.61536L6.78333 7.33203L11 11.5487L15.2167 7.33203L16.5 8.61536L11 14.1154Z"
        fill={color}
      />
    </g>
  </svg>
);

const ChevronUpIcon = ({ color }: { color: string }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_lang_chevron_up"
      style={{ maskType: "alpha" }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="22"
      height="22"
    >
      <rect
        width="22"
        height="22"
        transform="matrix(1 0 0 -1 0 22)"
        fill="#D9D9D9"
      />
    </mask>
    <g mask="url(#mask0_lang_chevron_up)">
      <path
        d="M11 7.88464L5.5 13.3846L6.78333 14.668L11 10.4513L15.2167 14.668L16.5 13.3846L11 7.88464Z"
        fill={color}
      />
    </g>
  </svg>
);

const LanguageSwitcher = () => {
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [isPersistingLocale, setIsPersistingLocale] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations("language_switcher");
  const { isEnabled } = useOptionalFeatureFlags();

  async function onSelectChange(locale: Locale) {
    if (params.locale === locale || isPersistingLocale) {
      return;
    }

    const query = searchParams.toString();
    const fullPath = `${pathname}${query ? `?${query}` : ""}`;

    setIsPersistingLocale(true);

    try {
      // Runs for signed-out visitors too: the preference cookie is what makes a
      // choice survive the Auth0 round-trip, so skipping this on the sign-in
      // page would silently discard it.
      const response = await updateUserLanguage(locale);

      if (response.error) {
        console.error("Failed to persist language preference:", response.error);
        toast.error(response.error.message || "Failed to update language.");
        return;
      }

      startTransition(() => {
        router.replace(fullPath, { locale });
      });
    } catch (error) {
      console.error("Unexpected error updating language preference:", error);
      toast.error("Failed to update language.");
    } finally {
      setIsPersistingLocale(false);
    }
  }

  const isDisabled = isPending || isPersistingLocale;
  const iconColor = isOpen ? "#2186C4" : "#737373";

  const localeLabels = Object.fromEntries(
    routing.locales.map((locale) => [locale, t(`dropdown.languages.${locale}`)])
  ) as Record<Locale, string>;
  const currentLabel =
    localeLabels[params.locale as Locale] ?? (params.locale as string);

  const offeredLocales = offeredLocalesFor(params.locale as string, isEnabled);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild disabled={isDisabled}>
        <button
          className="flex cursor-pointer items-center gap-1 outline-none disabled:opacity-50"
        >
          <GlobeIcon color={iconColor} />
          <span
            style={{
              color: iconColor,
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "normal"
            }}
          >
            {currentLabel}
          </span>
          {isOpen ? (
            <ChevronUpIcon color={iconColor} />
          ) : (
            <ChevronDownIcon color={iconColor} />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-40">
        {offeredLocales.map((locale) => (
          <DropdownMenuCheckboxItem
            key={locale}
            onClick={() => onSelectChange(locale)}
            checked={params.locale === locale}
          >
            {localeLabels[locale]}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { LanguageSwitcher };
